import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { getSessionToken, sendAnalyticsEvent } from '../services/api';
import { useLocation } from 'react-router-dom';

const LOCAL_STORAGE_SESSION_TOKEN_KEY = 'tcs_session_token';
const HEARTBEAT_INTERVAL = 5000; // 5 seconds

const AnalyticsContext = createContext();

export const AnalyticsProvider = ({ children }) => {
    const [sessionToken, setSessionToken] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const location = useLocation();
    const currentPage = useRef(location.pathname);
    const pageStartTime = useRef(Date.now());
    const heartbeatIntervalRef = useRef(null);

    // Fetch or retrieve session token
    useEffect(() => {
        const initializeSessionToken = async () => {
            try {
                // Check if session token exists in localStorage
                let token = localStorage.getItem(LOCAL_STORAGE_SESSION_TOKEN_KEY);

                // If not, fetch a new token
                if (!token) {
                    token = await getSessionToken({
                        startPage: currentPage.current,
                        referrer: document.referrer || 'direct',
                        timestamp: pageStartTime.current,
                    });
                    localStorage.setItem(LOCAL_STORAGE_SESSION_TOKEN_KEY, token);
                }

                setSessionToken(token);
                setIsLoading(false);

                // Send initial page view event
                sendAnalyticsEvent(token, {
                    eventType: 'pageView',
                    page: currentPage.current,
                    timestamp: Date.now(),
                });
            } catch (error) {
                console.error('Failed to initialize session token:', error);
                setIsLoading(false);
            }
        };

        initializeSessionToken();

        // Cleanup function
        return () => {
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
            }
        };
    }, []);

    // Set up heartbeat timer for page time tracking
    useEffect(() => {
        if (!sessionToken) return;

        // Send heartbeat with time on page
        const sendHeartbeat = () => {
            const currentTime = Date.now();
            const timeOnPage = currentTime - pageStartTime.current;

            sendAnalyticsEvent(sessionToken, {
                eventType: 'heartbeat',
                page: currentPage.current,
                timeOnPage: timeOnPage,
                timestamp: currentTime,
            }).catch(error => console.error('Failed to send heartbeat:', error));
        };

        // Set up interval for heartbeat
        heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

        // Cleanup
        return () => {
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
            }
        };
    }, [sessionToken]);

    // Track page changes
    useEffect(() => {
        if (!sessionToken) return;

        const newPage = location.pathname;

        // Only track if the page actually changed
        if (newPage !== currentPage.current) {
            // Send event for leaving the previous page
            const currentTime = Date.now();
            const timeOnPreviousPage = currentTime - pageStartTime.current;

            sendAnalyticsEvent(sessionToken, {
                eventType: 'pageExit',
                page: currentPage.current,
                timeOnPage: timeOnPreviousPage,
                timestamp: currentTime,
            }).catch(error => console.error('Failed to send page exit event:', error));

            // Update refs for new page
            currentPage.current = newPage;
            pageStartTime.current = currentTime;

            // Send event for entering the new page
            sendAnalyticsEvent(sessionToken, {
                eventType: 'pageView',
                page: newPage,
                timestamp: currentTime,
            }).catch(error => console.error('Failed to send page view event:', error));
        }
    }, [location.pathname, sessionToken]);

    // Track button clicks
    const trackButtonClick = useCallback((buttonName) => {
        if (!sessionToken) return;

        sendAnalyticsEvent(sessionToken, {
            eventType: 'buttonClick',
            buttonName: buttonName,
            page: currentPage.current,
            timestamp: Date.now(),
        }).catch(error => console.error('Failed to track button click:', error));
    }, [sessionToken]);

    // General event tracking function
    const trackEvent = useCallback((eventType, eventData = {}) => {
        if (!sessionToken) return;

        sendAnalyticsEvent(sessionToken, {
            eventType,
            page: currentPage.current,
            timestamp: Date.now(),
            ...eventData,
        }).catch(error => console.error(`Failed to track ${eventType} event:`, error));
    }, [sessionToken]);

    return (
        <AnalyticsContext.Provider
            value={{
                trackButtonClick,
                trackEvent,
                isAnalyticsLoading: isLoading
            }}
        >
            {children}
        </AnalyticsContext.Provider>
    );
};

export const useAnalytics = () => {
    const context = useContext(AnalyticsContext);
    if (!context) {
        throw new Error('useAnalytics must be used within an AnalyticsProvider');
    }
    return context;
}

export default AnalyticsContext;